import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { usePriceLive } from '@ui/hooks/price/usePriceLive';
import { useTradePairInfosByPairBase } from '@ui/hooks/trade/useTradePairInfos';
import { Text } from '@ui/lib/park/ui/text';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { useTranslation } from 'next-i18next';
import { HStack } from 'styled-system/jsx';

import { usePositionListContext } from './context';
import { valueStyle } from './styles';
import { PositionMarketData } from './types';

export const CloseFee = ({
  position,
  marketData,
  ...props
}: {
  position: Position;
  marketData?: PositionMarketData;
}) => {
  if (marketData) {
    return (
      <CloseFeeValue position={position} marketData={marketData} {...props} />
    );
  }

  return <CloseFeeWithHooks position={position} {...props} />;
};

const CloseFeeWithHooks = ({ position, ...props }: { position: Position }) => {
  const { t } = useTranslation();
  const pairMarketPrice = usePriceLive(position.pair.baseSymbol);

  const collateralMarketPrice = usePayTokenPriceLatest(
    position.collateralToken,
  );

  const tradePair = useTradePairInfosByPairBase(position.pair.address);

  const { feeConfig } = usePositionListContext();
  const marketData = {
    pairMarketPrice,
    collateralMarketPrice,
    tradePair,
  };

  return (
    <CloseFeeValue
      position={position}
      marketData={marketData}
      feeConfig={feeConfig}
      freeText={t('common.Free')}
      {...props}
    />
  );
};

const CloseFeeValue = ({
  position,
  marketData,
  feeConfig: feeConfigProp,
  freeText,
  ...props
}: {
  position: Position;
  marketData: PositionMarketData;
  feeConfig?: ReturnType<typeof usePositionListContext>['feeConfig'];
  freeText?: string;
}) => {
  const { t } = useTranslation();
  const feeConfig = feeConfigProp ?? marketData.tradePair?.feeConfig;
  const pairMarketPrice = marketData.pairMarketPrice;
  const collateralMarketPrice = marketData.collateralMarketPrice;
  const tradePair = marketData.tradePair;

  if (!pairMarketPrice || !feeConfig || !collateralMarketPrice || !tradePair)
    return null;

  const closeFeeRv = position.getClosePositionFee({
    ...feeConfig,
    pairMarketPrice,
    collateralMarketPrice,
    tradePair,
  });

  const closeFee = closeFeeRv.finalCloseFee.times(-1);

  const baseCloseFee = closeFeeRv.closeFee.times(-1);

  return (
    <HStack>
      {!closeFee.eq(baseCloseFee) && (
        <AmountUsd
          textDecoration="line-through"
          value={baseCloseFee}
          data-cy-value={baseCloseFee}
          css={valueStyle}
          {...props}
        />
      )}
      {closeFee.isZero() ? (
        <Text css={{ ...valueStyle, color: 'system.successLight' }}>
          {freeText ?? t('common.Free')}
        </Text>
      ) : (
        <AmountUsd
          value={closeFee}
          data-cy-value={closeFee}
          css={valueStyle}
          {...props}
        />
      )}
    </HStack>
  );
};
